/**
 * @module Skeleton
 * @description A loading placeholder component that mimics the shape of content while it loads.
 * Supports various shapes and sizes for different content types.
 * 
 * @typedef {'default' | 'card' | 'text' | 'title' | 'avatar' | 'button' | 'input'} SkeletonVariant
 * 
 * @typedef {Object} SkeletonProps
 * @property {string} [className] - Additional CSS classes
 * @property {SkeletonVariant} [variant='default'] - Skeleton style variant
 * 
 * @example
 * ```jsx
 * // Basic skeleton
 * <Skeleton className="h-12 w-12" />
 * 
 * // Text skeleton
 * <Skeleton variant="text" />
 * 
 * // Card skeleton with compound component
 * <Skeleton.Card />
 * ```
 */

import { cn } from '@lib/utils';
import PropTypes from 'prop-types';
import { brandGuide } from '@styles/brandGuide';

/**
 * Skeleton component
 * 
 * @param {SkeletonProps} props - Component props
 * @returns {JSX.Element} Skeleton placeholder
 */
export function Skeleton({
  className,
  variant = 'default',
  ...props
}) {
  return (
    <div
      className={cn(
        'animate-pulse rounded-md bg-gray-200',
        brandGuide.components.skeleton.variants[variant],
        className
      )}
      {...props}
    />
  );
}

Skeleton.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf([
    'default',
    'card',
    'text',
    'title',
    'avatar',
    'button',
    'input'
  ])
};

/**
 * Compound component for card skeleton layout
 * 
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Card skeleton layout
 */
Skeleton.Card = function SkeletonCard({ className }) {
  return (
    <div className={cn('space-y-3', className)}>
      <Skeleton variant="card" />
      <Skeleton variant="title" />
      <Skeleton variant="text" />
      <Skeleton variant="text" className="w-4/5" />
    </div>
  );
};

Skeleton.Card.propTypes = {
  className: PropTypes.string
};

/**
 * Compound component for form skeleton layout
 * 
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Form skeleton layout
 */
Skeleton.Form = function SkeletonForm({ className }) {
  return (
    <div className={cn('space-y-4', className)}>
      <div>
        <Skeleton variant="text" className="w-24 mb-2" />
        <Skeleton variant="input" />
      </div>
      <div>
        <Skeleton variant="text" className="w-24 mb-2" />
        <Skeleton variant="input" />
      </div>
      <div>
        <Skeleton variant="text" className="w-24 mb-2" />
        <Skeleton variant="input" className="h-32" />
      </div>
      <Skeleton variant="button" className="mt-4" />
    </div>
  );
};

Skeleton.Form.propTypes = {
  className: PropTypes.string
}; 